import { ShipmentStatusFilter } from "@freightsimple/generated-carriers-openapi-client/models/ShipmentStatusFilter";
import type { LocalDate } from "@freightsimple/time/branded-types";

/**
 * The shipment list's filters, as the screen holds them.
 *
 * Filtering moved to the API when the list became paginated: filtering a page in
 * the browser would answer a different question from the one asked, and the
 * total beside it would be wrong.
 */
export interface ShipmentFilters {
  search: string;
  status: ShipmentStatusFilter;
  dates: [LocalDate, LocalDate] | undefined;
  service: string | undefined;
}

export const EMPTY_SHIPMENT_FILTERS: ShipmentFilters = {
  search: "",
  status: ShipmentStatusFilter.All,
  dates: undefined,
  service: undefined,
};

/**
 * The four things a carrier looks for.
 *
 * Two are shipment states; the other two are conditions we derive — whether an
 * operational issue is open, and whether the invoice is under audit. Those two
 * are what a carrier is usually actually hunting for, which is why they sit
 * alongside the states rather than in a separate control.
 */
export const SHIPMENT_STATUS_FILTERS = [
  { label: "All", value: ShipmentStatusFilter.All },
  { label: "In Transit", value: ShipmentStatusFilter.InTransit },
  { label: "Delivered", value: ShipmentStatusFilter.Delivered },
  { label: "Issue Open", value: ShipmentStatusFilter.IssueOpen },
  { label: "In Audit", value: ShipmentStatusFilter.InAudit },
];

export function isFiltering(filters: ShipmentFilters): boolean {
  return (
    filters.search.trim() !== "" ||
    filters.status !== ShipmentStatusFilter.All ||
    filters.dates !== undefined ||
    filters.service !== undefined
  );
}

/**
 * Turns the screen's filters into request parameters.
 *
 * Empty values are omitted rather than sent as empty strings, so an unfiltered
 * list is a plain request and the URL stays readable in the network tab.
 */
export function toShipmentQuery(filters: ShipmentFilters): {
  search?: string;
  status?: ShipmentStatusFilter;
  fromDate?: string;
  toDate?: string;
  service?: string;
} {
  const search = filters.search.trim();
  return {
    ...(search === "" ? {} : { search }),
    ...(filters.status === ShipmentStatusFilter.All
      ? {}
      : { status: filters.status }),
    // A LocalDate is already the YYYY-MM-DD the API wants, so it goes on the
    // wire as-is. Running it through a display formatter is what sent
    // "Fri May 1st" and got a 400 back.
    ...(filters.dates === undefined
      ? {}
      : { fromDate: filters.dates[0], toDate: filters.dates[1] }),
    ...(filters.service === undefined ? {} : { service: filters.service }),
  };
}

/** A one-line summary of what the list is showing, so a page never implies it is everything. */
export function describeShowing(
  shown: number,
  total: number,
  page: number,
  pageSize: number,
): string {
  if (total === 0) return "No shipments";
  const first = page * pageSize + 1;
  const last = first + shown - 1;
  if (total <= shown && page === 0) {
    return `${total.toLocaleString("en-CA")} shipment${total === 1 ? "" : "s"}`;
  }
  return `${first.toLocaleString("en-CA")}–${last.toLocaleString("en-CA")} of ${total.toLocaleString("en-CA")}`;
}
